import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLang } from "@/i18n";
import Hero from "@/components/Hero";import Reveal from "@/components/Reveal";
import PropertyGallery from "@/components/PropertyGallery";
import { ServicesGrid, ProvidersGrid } from "@/components/IconGrids";

export default function Home() {
  const { t, simg } = useLang();
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100);
    }
  }, [location.hash]);

  return (
    <main data-testid="home-page">
      <Hero />

      {/* Présentation */}
      <section id="conciergerie" data-testid="about-section" className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-20 items-center">
          <Reveal>
            <p className="eyebrow">{t.about.eyebrow}</p>
            <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.15] text-maquis-deep">
              {t.about.title}
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="space-y-5 text-base sm:text-lg leading-relaxed text-justify text-maquis-olive">
              <p>{t.about.p1}</p>
              <p>{t.about.p2}</p>
              <p className="font-serif italic text-maquis-deep">{t.about.p3}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Propriétaires / Voyageurs */}
      <section data-testid="owners-travelers-section" className="mx-auto max-w-7xl px-5 sm:px-8 pb-24 sm:pb-28">
        <div className="grid gap-14 md:grid-cols-2 lg:gap-24">
        <div className="relative min-h-[52vh] overflow-hidden rounded-2xl group">
          <img src={simg("hillTown")} alt="Maison corse" loading="lazy"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-maquis-deep/85 via-maquis-deep/35 to-maquis-deep/20" />
          <div className="relative flex h-full flex-col justify-end p-8 sm:p-14 text-casse">
            <Reveal>
              <p className="eyebrow text-sable">{t.owners.eyebrow}</p>
              <h3 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl">{t.owners.title}</h3>
              <p className="mt-5 max-w-md text-sm sm:text-base leading-relaxed text-casse/85">{t.owners.text}</p>
              <Link to="/contact?sujet=gestion" data-testid="owners-cta"
                className="btn-outline-elegant mt-8 w-fit text-casse hover:bg-casse hover:text-maquis-deep">
                {t.owners.cta}
              </Link>
            </Reveal>
          </div>
        </div>
        <div className="relative min-h-[52vh] overflow-hidden rounded-2xl group">
          <img src={simg("boatSail")} alt="Voilier en Méditerranée" loading="lazy"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-mer/85 via-mer/35 to-mer/20" />
          <div className="relative flex h-full flex-col justify-end p-8 sm:p-14 text-casse">
            <Reveal>
              <p className="eyebrow text-sable">{t.travelers.eyebrow}</p>
              <h3 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl">{t.travelers.title}</h3>
              <p className="mt-5 max-w-md text-sm sm:text-base leading-relaxed text-casse/85">{t.travelers.text}</p>
              <Link to="/locations" data-testid="travelers-cta"
                className="btn-outline-elegant mt-8 w-fit text-casse hover:bg-casse hover:text-maquis-deep">
                {t.travelers.cta}
              </Link>
            </Reveal>
          </div>
        </div>
        </div>
      </section>

      {/* Services */}
      <section data-testid="services-section" className="mx-auto max-w-7xl px-5 sm:px-8 pt-14 pb-24 sm:pt-20 sm:pb-32">
        <Reveal>
          <p className="eyebrow">{t.services.eyebrow}</p>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl lg:whitespace-nowrap text-maquis-deep">{t.services.title}</h2>
          <p className="mt-4 text-base text-maquis-soft">{t.services.sub}</p>
        </Reveal>
        <div className="mt-12">
          <ServicesGrid />
        </div>
      </section>

      {/* Citation + galerie des biens */}
      <section data-testid="quote-section" className="bg-ivoire">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 pt-24 sm:pt-32 text-center">
          <Reveal>
            <p className="font-serif italic text-3xl sm:text-4xl lg:text-5xl leading-snug text-maquis-deep">
              « {t.gallery.quote} »
            </p>
          </Reveal>
        </div>
        <div className="mx-auto max-w-7xl px-5 sm:px-8 pb-24 sm:pb-32">
          <PropertyGallery />
        </div>
      </section>

      {/* Prestataires */}
      <section data-testid="providers-section" className="bg-casse">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32">
          <Reveal className="max-w-2xl">
            <p className="eyebrow">{t.providers.eyebrow}</p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-maquis-deep">{t.providers.title}</h2>
            <p className="mt-4 text-base text-maquis-soft">{t.providers.sub}</p>
          </Reveal>
          <div className="mt-12">
            <ProvidersGrid />
          </div>
        </div>
      </section>
    </main>
  );
}
